import { ComponentDefinitionBuilder } from 'nest/platform/models';
import { CHATSPOT_SIDEBAR_COMPONENT, NC_PLATFORM_NAME, Tags } from '../constants';
import { getIframeSrc, getStyle } from './internal';
import { capabilities } from '../../capabilities';
import { args } from 'nest/common/args';
import { onCopilotToggled } from './internal/onCopilotToggle';
import { isInIframeInHubSpot } from './internal/isInIframeInHubSpot';
// We know this function won't always work, it's wrapped in a try/catch but we
// want to slowly adopt this new behavior as apps adopt quartz.
// @bend-expect quartz-outside-quartz See above
import { getCopilotLayoutType as getCopilotLayoutFromQuartz, getCopilotSidebarSide as getCopilotSidebarSideFromQuartz, getBreezeAssistantSidebarBackground as getBreezeAssistantSidebarBackgroundFromQuartz, setCopilotSidebarSide as setCopilotSidebarSideInQuartz } from 'quartz/framework/copilot/service';
const COPILOT_SIDEBAR_CLASS = '.copilot-sidebar-container';
const QUARTZ_SIDEBAR_CLASS = '.quartz-grid-sidebar-container';

// TODO: remove once all consumers have migrated to conversationId and the BA/Ably path is fully retired.
export const COPILOT_THREAD_ID_QUERY_PARAM = 'copilotThreadId';
export const CUSTOM_ASSISTANT_QUERY_PARAM = 'customAssistantId';
export const COPILOT_ACTION_QUERY_PARAM = 'copilotAction';
export const BREEZE_PROJECT_ID_QUERY_PARAM = 'breezeProjectId';
export const ARTIFACT_ID_QUERY_PARAM = 'artifactId';
export const BREEZE_ENGINE_CONVERSATION_ID_QUERY_PARAM = 'conversationId';
export const hostConfigPropsSchema = {
  hostConfig: args.optional(args.object({
    sidebarSection: args.optional(args.object({
      visibility: args.optional(args.or(args.literal('hidden'), args.literal('default')))
    })),
    chatInput: args.optional(args.object({
      placeholder: args.optional(args.string())
    }))
  }))
};
const QUERY_PARAM_TO_PROP_NAME = {
  [COPILOT_THREAD_ID_QUERY_PARAM]: 'threadIdQueryParam',
  [COPILOT_ACTION_QUERY_PARAM]: 'copilotActionQueryParam',
  [CUSTOM_ASSISTANT_QUERY_PARAM]: 'customAssistantQueryParam',
  [BREEZE_PROJECT_ID_QUERY_PARAM]: 'breezeProjectIdQueryParam',
  [ARTIFACT_ID_QUERY_PARAM]: 'artifactIdQueryParam',
  [BREEZE_ENGINE_CONVERSATION_ID_QUERY_PARAM]: 'conversationIdQueryParam'
};
function getQueryParamInitialProps() {
  const urlQueryParams = new URLSearchParams(window.location.search);
  const params = Object.keys(QUERY_PARAM_TO_PROP_NAME);
  const props = {};
  for (const param of params) {
    props[QUERY_PARAM_TO_PROP_NAME[param]] = urlQueryParams.get(param) || '';
  }
  return props;
}
const getShouldStartOpen = () => {
  try {
    // Local storage key for whether Copilot was previously open when the page loaded (i.e. open after a page reload)
    // Values can be one of the following integers: 0 | 1 | 2
    // 0 = closed | 1 = open | 2 = open expanded view
    const copilotInitialOpenSessionStorageValue = sessionStorage.getItem('copilot-initialize-open');
    return copilotInitialOpenSessionStorageValue === '1' || copilotInitialOpenSessionStorageValue === '2';
  } catch (e) {
    return false;
  }
};
const getCopilotLayoutType = () => {
  // Since not all apps use quartz this won't always be set, we just want to use
  // it if it is
  try {
    return getCopilotLayoutFromQuartz();
  } catch (e) {
    // We don't want to default this here. It will override the value from the
    // channel if we do.
    return undefined;
  }
};
const getSidebarBackground = () => {
  // Since not all apps use quartz this won't always be set, we just want to use
  // it if it is
  try {
    return getBreezeAssistantSidebarBackgroundFromQuartz();
  } catch (e) {
    return undefined;
  }
};

// disableCopilotWhenEmbedded is set by quartz's configureNavLoader and
// signals that this app opts into disabling copilot when embedded.
// isInIframeInHubSpot checks if the app is in a same-origin iframe.
const getIsCopilotDisabledForEmbeddedApp = () => {
  var _hubspot;
  return ((_hubspot = window.hubspot) === null || _hubspot === void 0 || (_hubspot = _hubspot.copilot) === null || _hubspot === void 0 ? void 0 : _hubspot.disableCopilotWhenEmbedded) && isInIframeInHubSpot();
};
const getSidebarSide = () => {
  var _hubspot2;
  const override = (_hubspot2 = window.hubspot) === null || _hubspot2 === void 0 || (_hubspot2 = _hubspot2.copilot) === null || _hubspot2 === void 0 ? void 0 : _hubspot2.sidebarSide;
  if (override === 'left' || override === 'right') return override;
  try {
    return getCopilotSidebarSideFromQuartz();
  } catch (e) {
    return 'right';
  }
};
export const chatspotSidebarComponentDef = ComponentDefinitionBuilder.builder(CHATSPOT_SIDEBAR_COMPONENT).setSrc(getIframeSrc()).setPlatformName(NC_PLATFORM_NAME).setCapabilityDefs([capabilities.UPSERT_GLOBAL_STYLES_CAP, capabilities.RESTYLE_CAP, capabilities.HIDE_COMPONENT_CAP, capabilities.SHOW_COMPONENT_CAP, capabilities.UPDATE_QUERY_PARAM_CAP]).dangerouslySetLoadInstantly(() => !getIsCopilotDisabledForEmbeddedApp()).setInitiallyOpen(() => !getIsCopilotDisabledForEmbeddedApp() && getShouldStartOpen()).setIframeProps(() => ({
  style: getStyle(getSidebarSide() === 'left')
})).setFunctionsSchema(Object.assign({}, onCopilotToggled)).setPropsSchema(Object.assign({
  isSearchOpen: args.optional(args.bool()),
  threadIdQueryParam: args.optional(args.string()),
  copilotActionQueryParam: args.optional(args.string()),
  copilotLayoutType: args.optional(args.string()),
  sidebarBackground: args.optional(args.string()),
  customAssistantQueryParam: args.optional(args.string()),
  breezeProjectIdQueryParam: args.optional(args.string()),
  artifactIdQueryParam: args.optional(args.string()),
  conversationIdQueryParam: args.optional(args.string()),
  isLeftSidebar: args.optional(args.bool())
}, hostConfigPropsSchema)).setInitialProps(() => {
  const sidebarSide = getSidebarSide();
  try {
    setCopilotSidebarSideInQuartz(sidebarSide);
  } catch (e) {
    // quartz global state may not be available; ignore
  }
  return Object.assign({
    isSearchOpen: false
  }, getQueryParamInitialProps(), {
    copilotLayoutType: getCopilotLayoutType(),
    sidebarBackground: getSidebarBackground(),
    isLeftSidebar: sidebarSide === 'left'
  });
}).setRenderTarget(() => getIsCopilotDisabledForEmbeddedApp() ? null : document.querySelector(COPILOT_SIDEBAR_CLASS) || document.querySelector(QUARTZ_SIDEBAR_CLASS)).setTags([Tags.SIDE_PANEL]).build();